import { Device } from '@twilio/voice-sdk';
import { useCallback, useEffect, useState } from 'react';
import ormaApi from '../../services/OrmaApi';

async function getTwilioToken() {
  const res = await ormaApi.get("/api/calling/voice-token");
  return res.data;
}

/**
 * Custom hook to manage Twilio Voice Device lifecycle
 * @param {boolean} isActive - Whether the device should be initialized
 * @returns {object} Device state and methods
 */
export function useTwilioDevice(isActive) {
  const [device, setDevice] = useState(null);
  const [call, setCall] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize device when active, destroy when inactive
  useEffect(() => {
    if (isActive) {
      setLoading(true);
      setStatus('Initializing...');
      
      getTwilioToken()
        .then((data) => {
          const newDevice = new Device(data, {
            // Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
            // providing better audio quality in restrained network conditions. Opus will be default in 2.0.
            codecPreferences: ['opus', 'pcmu'],
            // Use fake DTMF tones client-side. Real tones are still sent to the other end of the call,
            // but the client-side DTMF tones are fake. This prevents the local mic capturing the DTMF tone
            // a second time and sending the tone twice. This will be default in 2.0.
            fakeLocalDTMF: true,
            // Use `enableRingingState` to enable the device to emit the `ringing`
            // state. The TwiML backend also needs to have the attribute
            // `answerOnBridge` also set to true in the `Dial` verb. This option
            // changes the behavior of the SDK to consider a call `ringing` starting
            // from the connection to the TwiML backend to when the recipient of
            // the `Dial` verb answers.
            enableRingingState: true,
            debug: true,
          });

          const handleRegistered = () => {
            setStatus('Ready for call.');
          };

          const handleError = (error) => {
            console.error('Received error: ', error);
            setStatus(
              'An Error occurred. Try refreshing or try again later.',
            );
          };

          // Handle token expiration and refresh
          const handleTokenWillExpire = async () => {
            try {
              const data = await getTwilioToken();
              newDevice.updateToken(data);
            } catch (error) {
              console.error('Failed to refresh token:', error);
              setStatus('Session expired. Please refresh the page.');
            }
          };

          newDevice.on('registered', handleRegistered);
          newDevice.on('error', handleError);
          newDevice.on('tokenWillExpire', handleTokenWillExpire);
          
          // Register the device to receive incoming calls and enable audio
          newDevice.register();

          setDevice(newDevice);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch voice token:', error);
          setStatus('Failed to initialize. Please refresh the page.');
          setLoading(false);
        });
    } else {
      // Clean up device when deactivated
      if (device) {
        // Only unregister if device is in registered state
        if (device.state === 'registered') {
          device.unregister();
        }
        device.destroy();
        setDevice(null);
        setCall(null);
        setStatus('');
        setLoading(false);
      }
    }

    // Cleanup on unmount or when deactivated
    return () => {
      if (device) {
        // Only unregister if device is in registered state
        if (device.state === 'registered') {
          device.unregister();
        }
        device.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Make a call
  const makeCall = useCallback(async (phoneNumber) => {
    if (!device) {
      console.error('Device not initialized');
      return;
    }

    try {
      // Ensure audio is registered on user interaction
      await device.audio?.incoming(true);
      
      const outgoingCall = await device.connect({
        params: {
          To: phoneNumber,
        },
      });
      setCall(outgoingCall);
      setStatus('Calling...');
      
      outgoingCall.on('accept', () => {
        setStatus('Connected – On Call.');
      });
      
      outgoingCall.on('disconnect', () => {
        setStatus('Call completed.');
        setCall(null);
      });
      
      outgoingCall.on('cancel', () => {
        setStatus('Call cancelled.');
        setCall(null);
      });
    } catch (error) {
      console.error('Failed to connect call:', error);
      setStatus('Failed to connect call.');
    }
  }, [device]);

  // Hang up current call
  const hangUp = useCallback(() => {
    if (call) {
      call.disconnect();
    }
  }, [call]);

  // Reset status
  const resetStatus = useCallback(() => {
    setStatus('Ready for call.');
  }, []);

  const isOnCall = device && status === 'Connected – On Call.';

  return {
    device,
    call,
    status,
    loading,
    isOnCall,
    makeCall,
    hangUp,
    resetStatus,
  };
}
